import React from "react";
import { Link } from "react-router-dom";
import { AlertCircle } from "lucide-react";

/**
 * Stack-ul de bannere de alertă pentru cardul fiecărui angajat:
 *  1. Date personale / documente lipsă
 *  2. Rapoarte lunare lipsă
 *  3. PSA nealocat
 *  4. Fișe de salariu lipsă
 *  5. Discrepanțe raport ↔ fișa salariu
 *  6. Status concediat + handover history
 *
 * Toate sunt presentational; primesc datele deja calculate prin props.
 */
export default function PersonAlertBanners({ p, audit, missing, missingDocs }) {
  return (
    <>
      {/* 1. Date personale / documente lipsă */}
      {(() => {
        const total = missing.length + missingDocs.length;
        if (total === 0) return null;
        const labels = [
          ...missing.map((m) => m.label),
          ...missingDocs.map((m) => `📎 ${m.label}`),
        ];
        const hasCritical = missingDocs.length > 0 || missing.length >= 3;
        const tone = hasCritical
          ? { wrap: "alert-banner-red border-2 border-red-500 text-red-900 ring-2 ring-red-300 shadow-lg shadow-red-200", icon: "text-red-700", cta: "text-red-800", pulse: "animate-pulse" }
          : { wrap: "alert-banner-orange border-2 border-orange-400 text-orange-900 shadow-md", icon: "text-orange-700", cta: "text-orange-800", pulse: "" };
        return (
          <div
            className={`mt-3 px-4 py-3 rounded-lg ${tone.wrap} flex items-start gap-3`}
            data-testid={`person-missing-banner-${p.id}`}
            title={`Lipsesc: ${labels.join(", ")}`}
          >
            <AlertCircle size={26} className={`shrink-0 mt-0.5 ${tone.icon} ${tone.pulse}`}/>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-extrabold uppercase tracking-wider">
                {hasCritical
                  ? `🚨 DOCUMENTE CRITICE LIPSĂ · ACȚIUNE URGENTĂ`
                  : missingDocs.length > 0 && missing.length === 0
                    ? `${missingDocs.length === 1 ? "Document lipsă" : `${missingDocs.length} documente lipsă`} · încarcă${missingDocs.length === 1 ? "-l" : "-le"} acum`
                    : `Date lipsă · introdu ${total === 1 ? "ce lipsește" : `cele ${total} elemente lipsă`}`}
              </div>
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {labels.map((label, i) => (
                  <span
                    key={`${label}-${i}`}
                    className={`text-[11px] px-2 py-0.5 rounded-full font-semibold ${hasCritical ? "bg-red-100 text-red-800 border border-red-200" : "bg-amber-100 text-amber-800 border border-amber-200"}`}
                  >
                    {label}
                  </span>
                ))}
              </div>
            </div>
            <span className={`text-xs font-bold ${tone.cta} shrink-0 self-center whitespace-nowrap`}>Completează →</span>
          </div>
        );
      })()}

      {/* 2. Rapoarte lunare lipsă */}
      {(() => {
        if (!audit || !audit.missing_months || audit.missing_months.length === 0) return null;
        const n = audit.missing_months.length;
        const isCritical = n >= 3;
        const monthNames = ["Ian", "Feb", "Mar", "Apr", "Mai", "Iun", "Iul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        const sample = audit.missing_months.slice(0, 4).map((ym) => {
          const [y, m] = ym.split("-");
          return `${monthNames[parseInt(m) - 1]} ${y.slice(2)}`;
        });
        const extra = n - sample.length;
        return (
          <div
            className={`mt-3 px-4 py-3 rounded-lg flex items-start gap-3 ${isCritical ? "alert-banner-red border-2 border-red-500 text-red-900 ring-2 ring-red-300 shadow-lg" : "alert-banner-orange border-2 border-orange-400 text-orange-900 shadow-md"}`}
            data-testid={`person-audit-banner-${p.id}`}
            title={`Rapoarte lunare lipsă: ${audit.missing_months.join(", ")}`}
          >
            <span className="text-2xl leading-none shrink-0">📋</span>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-extrabold uppercase tracking-wider">
                {isCritical ? `🚨 ${n} RAPOARTE LUNARE LIPSĂ` : `${n} ${n === 1 ? "raport lunar lipsă" : "rapoarte lunare lipsă"}`}
              </div>
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {sample.map((label, i) => (
                  <span key={`audit-${label}-${i}`} className={`text-[11px] px-2 py-0.5 rounded-full font-semibold ${isCritical ? "bg-red-100 text-red-800 border border-red-200" : "bg-amber-100 text-amber-800 border border-amber-200"}`}>
                    {label}
                  </span>
                ))}
                {extra > 0 && (
                  <span className={`text-[11px] px-2 py-0.5 rounded-full font-bold ${isCritical ? "bg-red-200 text-red-900" : "bg-amber-200 text-amber-900"}`}>
                    +{extra} mai multe
                  </span>
                )}
              </div>
            </div>
            <span className={`text-xs font-bold shrink-0 self-center whitespace-nowrap ${isCritical ? "text-red-800" : "text-orange-800"}`}>Generează →</span>
          </div>
        );
      })()}

      {/* 3. PSA NEALOCAT */}
      {(() => {
        const psaCount = audit?.psa_count;
        if (psaCount !== 0 || p.active === false) return null;
        return (
          <Link
            to={`/personal/${p.id}#psa`}
            onClick={(e) => e.stopPropagation()}
            className="mt-3 px-4 py-3 rounded-lg flex items-start gap-3 alert-banner-red border-2 border-red-500 text-red-900 ring-2 ring-red-300 shadow-lg animate-pulse-red-soft hover:bg-red-50/40 transition-colors"
            data-testid={`person-no-psa-banner-${p.id}`}
            title="Niciun echipament PSA nu este alocat acestei persoane"
          >
            <span className="text-2xl leading-none shrink-0">🦺</span>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-extrabold uppercase tracking-wider">⚠ PSA NEALOCAT</div>
              <div className="text-[12px] mt-1">
                Acest angajat nu are <strong>niciun echipament PSA</strong> alocat (mască, furtun, curea, cască, cizme, bocanci, hanorac, tricou etc.). Predă-i echipamentul necesar.
              </div>
            </div>
            <span className="text-xs font-bold shrink-0 self-center whitespace-nowrap text-red-800">Predă PSA →</span>
          </Link>
        );
      })()}

      {/* 4. Fișe de salariu LIPSĂ */}
      {(() => {
        const missingSlips = audit?.missing_slips || [];
        if (missingSlips.length === 0) return null;
        const sample = missingSlips.slice(0, 5);
        return (
          <div
            className="mt-3 px-4 py-3 rounded-lg flex items-start gap-3 alert-banner-red border-2 border-red-500 text-red-900 ring-2 ring-red-300 shadow-lg animate-pulse-red-soft"
            data-testid={`person-missing-slips-banner-${p.id}`}
            title={`Fișe de salariu lipsă pentru: ${missingSlips.join(", ")}`}
          >
            <span className="text-2xl leading-none shrink-0">💰</span>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-extrabold uppercase tracking-wider">
                ⚠ {missingSlips.length} FIȘĂ{missingSlips.length > 1 ? " DE SALARIU LIPSESC" : " DE SALARIU LIPSEȘTE"}
              </div>
              <div className="text-[12px] mt-1">
                Lună{missingSlips.length > 1 ? "i" : ""} fără fișă: <strong className="font-mono">{sample.join(", ")}</strong>
                {missingSlips.length > sample.length && (
                  <span className="text-[10px] text-red-700 italic"> +{missingSlips.length - sample.length} altele…</span>
                )}
              </div>
            </div>
            <span className="text-xs font-bold shrink-0 self-center whitespace-nowrap text-red-800">Încarcă →</span>
          </div>
        );
      })()}

      {/* 5. Discrepanțe raport ↔ fișa salariu */}
      {(() => {
        const mismatches = audit?.slip_mismatches || [];
        if (mismatches.length === 0) return null;
        const sample = mismatches.slice(0, 3);
        return (
          <div
            className="mt-3 px-4 py-3 rounded-lg flex items-start gap-3 alert-banner-red border-2 border-red-500 text-red-900 ring-2 ring-red-300 shadow-lg animate-pulse-red-soft"
            data-testid={`person-slip-mismatch-banner-${p.id}`}
            title={mismatches.map((m) => {
              if (m.kind === "empty_report") return `${m.month}: slip uploadat dar raport gol (0h${m.slip ? ` vs fișa ${m.slip}h` : ""})`;
              return `${m.month}: ${m.kind === "hours" ? "ore lucru" : "ore transport"} raport ${m.report}h vs fișa ${m.slip}h (diff ${m.diff > 0 ? "+" : ""}${m.diff}h)`;
            }).join(" · ")}
          >
            <span className="text-2xl leading-none shrink-0">⚠</span>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-extrabold uppercase tracking-wider">
                🚨 {mismatches.length} DISCREPANȚĂ{mismatches.length > 1 ? "E" : ""} RAPORT ↔ FIȘĂ SALARIU
              </div>
              <div className="text-[12px] mt-1 space-y-0.5">
                {sample.map((m, i) => (
                  <div key={`${m.month}-${m.kind}-${i}`} className="font-mono">
                    {m.kind === "empty_report" ? (
                      <>
                        • <strong>{m.month}</strong> · 📄 Slip uploadat dar <strong className="text-red-700">raport GOL</strong> (0h)
                        {m.slip != null && <>{" "}<span className="px-1 rounded bg-red-200 text-red-900 font-bold">fișa = {m.slip}h</span></>}
                      </>
                    ) : (
                      <>
                        • <strong>{m.month}</strong> · {m.kind === "hours" ? "Ore lucru" : "Ore transport"}: raport <strong>{m.report}h</strong> vs fișa <strong>{m.slip}h</strong>
                        {" "}<span className="px-1 rounded bg-red-200 text-red-900 font-bold">{m.diff > 0 ? "+" : ""}{m.diff}h</span>
                      </>
                    )}
                  </div>
                ))}
                {mismatches.length > sample.length && (
                  <div className="text-[10px] text-red-700 italic">+{mismatches.length - sample.length} altele…</div>
                )}
              </div>
            </div>
            <span className="text-xs font-bold shrink-0 self-center whitespace-nowrap text-red-800">Verifică →</span>
          </div>
        );
      })()}

      {/* 6. CONCEDIAT / INACTIV status + handover history
           4 categorii distincte:
             A. Concediat OFICIAL (data_concedierii setat) + handovers > 0  → verde "PREDAT COMPLET"
             B. Concediat OFICIAL + handovers === 0                          → albastru "NU A PRIMIT ECHIPAMENT"
             C. Inactiv (fără data concedierii) + handovers === 0            → galben/neutru "INACTIV · NU A PRIMIT"
             D. Inactiv (fără data concedierii) + handovers > 0 + psa===0   → verde-deschis "INACTIV · ECHIPAMENT PREDAT"
             E. orice + psa_count > 0                                        → roșu "ECHIPAMENT NEÎNTORS"
      */}
      {p.active === false && (() => {
        const psaCount = audit?.psa_count;
        const handovers = audit?.last_handovers || [];
        const isUnknownCount = typeof psaCount !== "number";
        const hasUnreturned = typeof psaCount === "number" && psaCount > 0;
        const everReceived = handovers.length > 0 || hasUnreturned;
        const isOfficiallyDismissed = !!p.data_concedierii;

        // Pick state
        let kind;
        if (hasUnreturned) {
          kind = "unreturned"; // E — roșu
        } else if (isUnknownCount && handovers.length === 0) {
          kind = "unknown"; // încă nu știm — gri
        } else if (isOfficiallyDismissed && !everReceived) {
          kind = "dismissed_never_received"; // B
        } else if (isOfficiallyDismissed && everReceived) {
          kind = "dismissed_handed_over"; // A
        } else if (!isOfficiallyDismissed && !everReceived) {
          kind = "inactive_never_received"; // C
        } else {
          kind = "inactive_handed_over"; // D
        }

        const STYLES = {
          unknown:                    { wrap: "border-zinc-300 bg-zinc-50 text-zinc-800",       icon: isOfficiallyDismissed ? "🚪" : "⏸️", title: isOfficiallyDismissed ? "CONCEDIAT (date PSA în curs de încărcare)" : "INACTIV (date PSA în curs de încărcare)", subtitle: null },
          dismissed_never_received:   { wrap: "border-blue-300 bg-blue-50 text-blue-900",       icon: "📋", title: "CONCEDIAT · NU A PRIMIT NICIODATĂ ECHIPAMENT", subtitle: "Persoana nu a apucat să primească PSA înainte de concediere." },
          dismissed_handed_over:      { wrap: "border-emerald-400 bg-emerald-50 text-emerald-900", icon: "✅", title: "CONCEDIAT · ECHIPAMENT PREDAT COMPLET", subtitle: null },
          inactive_never_received:    { wrap: "border-amber-300 bg-amber-50 text-amber-900",     icon: "⏸️", title: "INACTIV · NU A PRIMIT NICIODATĂ ECHIPAMENT", subtitle: "Persoana este temporar inactivă (ex: concediu medical). Nu este concediată oficial." },
          inactive_handed_over:       { wrap: "border-emerald-300 bg-emerald-50/70 text-emerald-900", icon: "⏸️", title: "INACTIV · ECHIPAMENT PREDAT", subtitle: "Persoana este temporar inactivă. Toate echipamentele au fost returnate." },
          unreturned:                 { wrap: "border-red-500 bg-red-50 text-red-900 ring-2 ring-red-200", icon: "⚠", title: `${isOfficiallyDismissed ? "CONCEDIAT" : "INACTIV"} · ${psaCount} echipament${psaCount === 1 ? "" : "e"} NEÎNTORS${psaCount === 1 ? "" : "E"}`, subtitle: null },
        };
        const s = STYLES[kind];

        return (
          <div
            className={`mt-3 px-4 py-3 rounded-lg border-2 ${s.wrap}`}
            data-testid={`person-dismissed-status-${p.id}`}
            data-kind={kind}
          >
            <div className="flex items-start gap-2">
              <span className="text-2xl leading-none shrink-0">{s.icon}</span>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-extrabold uppercase tracking-wider">
                  {s.title}
                </div>
                {s.subtitle && (
                  <div className="text-[11px] italic mt-0.5 opacity-90">{s.subtitle}</div>
                )}
                {p.data_concedierii && (
                  <div className="text-[11px] mt-0.5">
                    Data concedierii: <strong>{p.data_concedierii}</strong>
                  </div>
                )}
                {handovers.length > 0 && (
                  <div className="mt-2 space-y-0.5">
                    <div className="text-[10px] font-bold uppercase tracking-wider opacity-80">
                      📜 Ultimele predări de echipament:
                    </div>
                    {handovers.slice(0, 4).map((h, i) => {
                      const date = (h.ts || "").slice(0, 10);
                      return (
                        <div key={`${h.ts || ""}-${h.item_name || ""}-${i}`} className="text-[11px] font-mono">
                          • <strong>{date}</strong> · {h.item_name || "?"} → <strong>{h.to_name || h.to_location || "?"}</strong>
                          {h.user && <span className="opacity-70 ml-1">(de {h.user})</span>}
                        </div>
                      );
                    })}
                    {handovers.length > 4 && (
                      <div className="text-[10px] italic opacity-70">+{handovers.length - 4} altele… (vezi cronologia)</div>
                    )}
                  </div>
                )}
                {kind === "unreturned" && handovers.length === 0 && (
                  <div className="text-[11px] mt-1 italic">
                    Nu există încă predări înregistrate. Preia echipamentul rămas.
                  </div>
                )}
              </div>
              <span className={`text-xs font-bold shrink-0 self-center whitespace-nowrap ${
                kind === "unreturned" ? "text-red-800" :
                kind === "dismissed_handed_over" || kind === "inactive_handed_over" ? "text-emerald-800" :
                kind === "inactive_never_received" ? "text-amber-800" :
                kind === "dismissed_never_received" ? "text-blue-800" : "text-zinc-700"
              }`}>
                {kind === "unreturned" ? "Preia →" :
                 kind === "dismissed_handed_over" || kind === "inactive_handed_over" ? "OK ✓" :
                 kind === "inactive_never_received" ? "Inactiv" :
                 kind === "dismissed_never_received" ? "Fără PSA" : ""}
              </span>
            </div>
          </div>
        );
      })()}
    </>
  );
}
